import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog from '@material-ui/core/Dialog';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { saveAs } from 'file-saver';
import {
  buildDownloadUrlByNpwp,
  fetchInvoiceWrapper,
  formatNpwp,
  handleDownload,
  siteBaseUrl
} from './services/download.service';
import DownloadButton from './util-components/download-button';
import { GENERIC_PROP_TYPES } from './services/customization.service';

const useStyles = makeStyles((theme) => ({
  paperStyle: {
    padding: theme.spacing(6)
  },
  avatar: {
    width: theme.spacing(7),
    height: theme.spacing(7)
  },
  avatarBox: {
    display: 'flex',
    justifyContent: 'center'
  },
  companyName: {
    textAlign: 'center',
    fontWeight: 550
  },
  formHelper: {
    textAlign: 'center',
    fontWeight: 400,
    paddingBottom: `1rem`
  }
}));

GenericFakturDownloadForm.propTypes = GENERIC_PROP_TYPES;

function GenericFakturDownloadForm(props) {
  const classes = useStyles();
  const { pageDecorator } = props;

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const [providerNpwp, setProviderNpwp] = React.useState('');
  const [vendorNpwp, setVendorNpwp] = React.useState('');
  const [invoiceNumber, setInvoice] = React.useState('');

  const [isInvalidNpwpFormat, setIsInvalidNpwpFormat] = React.useState(false);
  const [errorText, setErrorText] = React.useState('Masukkan nomor NPWP sesuai format yang resmi');
  const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);

  const [providerInputFormat, setProviderInputFormat] = React.useState('number');
  const [vendorInputFormat, setVendorInputFormat] = React.useState('number');

  const fileUrl = () => buildDownloadUrlByNpwp(
    providerNpwp,
    vendorNpwp,
    invoiceNumber,
    siteBaseUrl()
  );

  const saveFile = (url) => {
    const invoiceFileName = `invoice_${invoiceNumber}_${providerNpwp}_${vendorNpwp}.pdf`;
    saveAs(url, invoiceFileName);
  };

  const displayReportErrorModal = async () => {
    setModalMessage(
      <>
        <DialogTitle style={{ textAlign: 'center', color: 'red' }}><b>Download Failed</b></DialogTitle>
        <DialogContent style={{ textAlign: 'center' }}>
          One or more fields – company, NPWP or invoice number – do not exist
          in our database
        </DialogContent>
      </>
    );
    setShowModal(true);
  };

  const handleNpwpChange = (event) => {
    const input = formatNpwp(event.target.value);
    const isInvalid = input.length < 20;
    setErrorText(isInvalid ? 'Harus terisi lengkap' : '');
    setIsInvalidNpwpFormat(isInvalid);
    setIsButtonDisabled(isInvalid);

    if (event.target.name === 'provider') {
      if (isInvalid) { setProviderInputFormat('number'); } else { setProviderInputFormat('text'); }
      setProviderNpwp(input);
    } else {
      if (isInvalid) { setVendorInputFormat('number'); } else { setVendorInputFormat('text'); }
      setVendorNpwp(input);
    }
  };

  const handleInvoiceChange = (event) => {
    setInvoice(event.target.value);
  };

  return (
    <>
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        {modalMessage}
      </Dialog>
      <Paper className={classes.paperStyle} elevation={0} variant="outlined">
        <Grid container={true} spacing={8}>
          <Grid item={true} container={true} xs={12} spacing={1}>
            <Grid item={true} xs={12}>
              <Box className={classes.avatarBox}>
                <Avatar
                  className={classes.avatar}
                  src={pageDecorator.image}
                  data-testid="customer-logo"
                />
              </Box>
            </Grid>
          </Grid>
          <form noValidate={true} autoComplete="off">
            <Typography className={classes.formHelper}>Lengkapi Formulir Pengunduhan</Typography>
            <Grid container={true} item={true} xs={12} spacing={4}>
              <Grid item={true} xs={12}>
                <TextField
                  id="providerNPWPField"
                  color="secondary"
                  className={classes.textBox}
                  type={providerInputFormat}
                  fullWidth={true}
                  multiline={false}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                  label="No. NPWP Penyedia Faktur Pajak"
                  placeholder="00.000.000.0-000.000"
                  value={providerNpwp}
                  onChange={handleNpwpChange}
                  inputProps={{ maxLength: 20 }}
                  error={isInvalidNpwpFormat}
                  helperText={errorText}
                  data-testid="provider-npwp-input"
                  name="provider"
                />
              </Grid>
              <Grid item={true} xs={12}>
                <TextField
                  id="clientNPWPField"
                  color="secondary"
                  className={classes.textBox}
                  type={vendorInputFormat}
                  fullWidth={true}
                  multiline={false}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                  label="No. NPWP Klien Faktur Pajak"
                  placeholder="00.000.000.0-000.000"
                  value={vendorNpwp}
                  onChange={handleNpwpChange}
                  inputProps={{ maxLength: 20 }}
                  error={isInvalidNpwpFormat}
                  helperText={errorText}
                  data-testid="vendor-npwp-input"
                  name="vendor"
                />
              </Grid>
              <Grid item={true} xs={12}>
                <TextField
                  id="commercialInvoiceNumber"
                  color="secondary"
                  className={classes.textBox}
                  type="text"
                  fullWidth={true}
                  multiline={false}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                  label="No. Faktur Komersial"
                  value={invoiceNumber}
                  onChange={handleInvoiceChange}
                  helperText="Masukkan nomor faktur komersial"
                  data-testid="invoice-input"
                />
              </Grid>
              <Grid item={true} xs={12}>
                <DownloadButton
                  disabled={isButtonDisabled}
                  onClick={() => handleDownload(
                    async () => fetchInvoiceWrapper(
                      fetch,
                      fileUrl()
                    ),
                    saveFile,
                    displayReportErrorModal
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Paper>
    </>
  );
}

export default GenericFakturDownloadForm;
